import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Compass,
  FileText,
  Download,
  File,
  ScrollText,
  BookOpen,
  Users,
  Calendar,
  MapPin,
  Mail
} from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const UnityLodge = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
  };

  // Gold color values
  const goldPrimary = '201, 169, 98'; // #C9A962

  // Subtle grid background
  const SubtleGrid = () => (
    <div className="absolute inset-0 z-0 overflow-hidden">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-[0.02]">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-amber-400 h-full" />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-[0.02]">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-amber-400 w-full" />
        ))}
      </div>
    </div>
  );

  // Therefore symbol for Masonic abbreviations
  const Therefore = () => (
    <span className="inline-block text-amber-400/80 mx-0.5">∴</span>
  );

  // Delta symbol component
  const DeltaSymbol = ({ size = 'lg' }) => {
    const sizeClasses = {
      sm: 'text-2xl',
      md: 'text-4xl',
      lg: 'text-6xl',
      xl: 'text-8xl'
    };

    return (
      <motion.div 
        className="relative inline-flex items-center justify-center"
        animate={{ 
          filter: [`drop-shadow(0 0 8px rgba(${goldPrimary},0.2))`, `drop-shadow(0 0 16px rgba(${goldPrimary},0.4))`, `drop-shadow(0 0 8px rgba(${goldPrimary},0.2))`]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className={`${sizeClasses[size]} font-extralight text-amber-400`}>Δ</span>
      </motion.div>
    );
  };

  // Documents data
  const documents = [
    {
      category: "Governing Documents",
      icon: ScrollText,
      items: [
        { name: "Unity Lodge Charter", type: "PDF", size: "2.4 MB", file: "/documents/Unity_Lodge_Charter.pdf" },
        { name: "Bylaws & Standing Regulations", type: "PDF", size: "890 KB", file: "/documents/Unity_Lodge_Bylaws.pdf" },
        { name: "Officer Installation Ceremony", type: "PDF", size: "1.6 MB", file: "/documents/Unity_Lodge_Officer_Ceremony.pdf" }
      ]
    },
    {
      category: "Educational Materials",
      icon: BookOpen,
      items: [
        { name: "The Missing Hive Matriarch", type: "PDF", size: "3.1 MB", file: "/documents/Queen_Bee_Paper.pdf" },
        { name: "Compass Through Computation", type: "PDF", size: "2.9 MB", file: "/documents/Compass_Through_Computation__English.pdf" },
        { name: "On The Subject of the Supreme Lodge", type: "PDF", size: "1.5 MB", file: "/documents/Supreme_Lodge_Outline.pdf" },
        { name: "On Forming a New World Conference of Supreme Councils", type: "PDF", size: "1.5 MB", file: "/documents/WCOSC_Reformation_Outline.pdf" }
      ]
    },
    {
      category: "Lodge Operations",
      icon: FileText,
      items: [
        { name: "Meeting Schedule 2026", type: "PDF", size: "423 KB", file: "/documents/Unity_Lodge_Meeting_Schedule_2026.pdf" },
        { name: "Annual Report Template", type: "DOCX", size: "156 KB", file: "/documents/Unity_Lodge_Annual_Report_Template.pdf" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#080604] text-gray-100">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <ParticleBackground />
        <SubtleGrid />
        
        {/* Radial gradient */}
        <div className={`absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(${goldPrimary},0.06)_0%,_transparent_60%)]`} />

        <div className="container mx-auto px-6 z-20 relative py-32">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Delta emblem */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="relative inline-block">
                {/* Subtle rotating ring */}
                <motion.div
                  className="absolute inset-0 w-36 h-36 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
                >
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="48" fill="none" stroke={`rgba(${goldPrimary},0.08)`} strokeWidth="0.5" strokeDasharray="4 4" />
                  </svg>
                </motion.div>

                {/* Delta symbol */}
                <div className="relative z-10 w-28 h-28 flex items-center justify-center">
                  <DeltaSymbol size="xl" />
                </div>
              </div>
            </motion.div>

            {/* Lodge name */}
            <motion.div variants={itemVariants} className="mb-4">
              <h1 
                className="text-4xl md:text-5xl font-extralight tracking-[0.12em] text-white"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                UNITY LODGE
              </h1>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8">
              <div className="flex items-center justify-center gap-4">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500/40" />
                <span className="text-xl md:text-2xl font-light text-amber-400">No. Δ</span>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500/40" />
              </div>
            </motion.div>

            <motion.p
              variants={itemVariants}
              className="text-lg md:text-xl font-light text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed"
            >
              A lodge dedicated to exploring the intersection of Masonic tradition 
              and the ethical frameworks for emerging technologies.
            </motion.p>

            {/* Quick info */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-wrap justify-center gap-6 text-sm text-gray-500"
            >
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-amber-500/60" />
                <span>Mobile, Alabama</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-amber-500/60" />
                <span>Chartered 2025</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-6 h-10 border border-amber-500/20 rounded-full flex justify-center pt-2">
            <motion.div 
              className="w-1 h-2 bg-amber-400/40 rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Section header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-4"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                About the Lodge
              </h2>
              
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500/40" />
                <Compass size={16} className="text-amber-500/50" />
                <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-500/40" />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#0a0806]/60 border border-amber-500/10 rounded-lg p-8 md:p-12">
                <div className="space-y-6 text-gray-300 leading-relaxed">
                  <p>
                    Unity Lodge No. Δ was established to create a space where Freemasons working in 
                    technology, defense, and research can explore how the timeless principles of the 
                    Craft apply to the challenges of our era.
                  </p>

                  <p>
                    Our lodge operates under a unique charter that recognizes the particular needs of 
                    brethren whose professional work intersects with emerging technologies, artificial 
                    intelligence, and national security. We believe the moral architecture taught 
                    through Freemasonry provides essential guidance for those building the systems 
                    that will shape tomorrow's world.
                  </p>

                  <p>
                    The Delta designation reflects our focus on change and transformation—both the 
                    personal transformation central to the Masonic journey and the technological 
                    transformations we navigate in our professional lives.
                  </p>

                  <p className="text-amber-400/70 italic border-l-2 border-amber-500/30 pl-6">
                    "In unity, we build. In wisdom, we guide. In light, we advance."
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Message from the W∴M∴ Section */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-transparent via-amber-950/5 to-transparent">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Section header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-4"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                Message from the W<Therefore />M<Therefore />
              </h2>
              
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500/40" />
                <Compass size={16} className="text-amber-500/50" />
                <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-500/40" />
              </div>
            </motion.div>

            {/* Message content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#0a0806]/60 border border-amber-500/10 rounded-lg p-8 md:p-12">
                <div className="space-y-6 text-gray-300 leading-relaxed">
                  <p className="text-lg">
                    <span className="text-amber-400 font-medium">Brethren,</span>
                  </p>

                  <p>
                    Unity Lodge No. Δ represents something new in Freemasonry—not a departure from 
                    tradition, but an application of our ancient principles to the questions our 
                    generation must answer.
                  </p>

                  <p>
                    Many of us work at the frontiers of technology, where the decisions we make today 
                    will echo for decades. The Square and Compasses have always guided builders. Now 
                    they guide those of us who build in code, in silicon, and in the architectures 
                    of artificial intelligence.
                  </p>

                  <p>
                    Our lodge offers fellowship with brethren who understand both the weight of this 
                    responsibility and the light that Masonic wisdom can provide. Here, we discuss 
                    not only the ritual but its application—how do we act with integrity when 
                    designing systems that will affect millions? How do we balance innovation with 
                    the moral foundations our Craft teaches?
                  </p>

                  <p>
                    If these questions resonate with you, I invite you to learn more about Unity Lodge.
                  </p>

                  <div className="pt-6 border-t border-amber-500/10">
                    <p className="text-sm text-gray-400">
                      Fraternally,
                    </p>
                    <p className="text-amber-400 mt-2 font-light tracking-wider">
                      — T<Therefore />D<Therefore />, W<Therefore />M<Therefore />
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Unity Lodge No. Δ</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-4"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Lodge Documents
            </h2>
            
            <p className="text-gray-500 text-sm tracking-wider max-w-xl mx-auto">
              Access official lodge materials and educational resources.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {documents.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="bg-amber-900/5 border border-amber-500/10 rounded-lg overflow-hidden"
              >
                {/* Category header */}
                <div className="p-4 border-b border-amber-500/10 bg-amber-900/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-900/30 flex items-center justify-center">
                      <category.icon size={20} className="text-amber-400" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-white">{category.category}</h3>
                      <p className="text-xs text-gray-500">{category.items.length} documents</p>
                    </div>
                  </div>
                </div>

                {/* Document list */}
                <div className="divide-y divide-amber-500/5">
                  {category.items.map((doc, docIndex) => (
                    <motion.a
                      key={docIndex}
                      href={doc.file}
                      download
                      whileHover={{ backgroundColor: 'rgba(201, 169, 98, 0.05)' }}
                      className="p-4 flex items-center justify-between group cursor-pointer transition-colors block"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                          <File size={14} className="text-amber-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-gray-300 group-hover:text-white transition-colors truncate">
                            {doc.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] font-mono text-amber-500/50">{doc.type}</span>
                            <span className="text-[10px] text-gray-600">•</span>
                            <span className="text-[10px] text-gray-600">{doc.size}</span>
                          </div>
                        </div>
                      </div>
                      
                      <Download size={14} className="text-amber-500/40 group-hover:text-amber-400 transition-colors ml-2 flex-shrink-0" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <ParticleBackground />
        <SubtleGrid />
        
        <div className={`absolute inset-0 bg-gradient-to-b from-transparent via-amber-900/5 to-transparent`} />

        <div className="container mx-auto px-6 relative z-20">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div className="inline-block mb-8">
              <DeltaSymbol size="lg" />
            </motion.div>
            
            <h2 
              className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-6"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Learn More
            </h2>
            
            <p className="text-gray-400 mb-10">
              Unity Lodge welcomes inquiries from Master Masons interested in our work. 
              We also welcome correspondence from those exploring Freemasonry for the first time.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group px-10 py-4 bg-gradient-to-r from-amber-900/40 to-amber-800/40 hover:from-amber-800/50 hover:to-amber-700/50 text-white font-medium tracking-wider rounded transition-all duration-300 flex items-center justify-center border border-amber-500/20 hover:border-amber-400/40"
              >
                <Mail size={16} className="mr-2" />
                Contact the Lodge
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
              </Link>
            </div>

            <p className="text-xs text-gray-600 mt-8">
              2 B 1 A S K 1
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UnityLodge;